import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface SearchableSelectOption {
  value: string;
  /** Text shown in the dropdown and, once chosen, in the input. */
  label: string;
  /** Text matched against the query. Defaults to `label`. */
  searchText?: string;
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  /** Input placeholder shown when nothing is selected. */
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  /** Classes for the text input so callers keep their own sizing/border. */
  className?: string;
  /** Extra guidance shown under the "no results" message (e.g. a data hint). */
  noResultsHint?: string;
  'aria-label'?: string;
}

const DEFAULT_INPUT_CLASS =
  'w-full px-3 py-2 border border-line bg-surface text-theme-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';

/**
 * A single combobox control: the search box and the picker are the same field.
 * Typing filters the options by substring (matching anywhere, not just the
 * start) and removes the rest; clicking or pressing Enter selects. The input
 * shows the live query while open and the chosen label once closed, so there is
 * no second control to manage. Works on touch/iPad (custom listbox, not a
 * native <select>).
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder,
  required,
  disabled,
  id,
  className,
  noResultsHint,
  'aria-label': ariaLabel,
}: SearchableSelectProps) {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  // Screen-fixed coordinates for the dropdown. The list is portaled to <body>
  // so an ancestor's `overflow-hidden` (e.g. the inspection zone card) can't
  // clip it to a couple of rows.
  const [menuPos, setMenuPos] = useState<
    { left: number; width: number; top: number | null; bottom: number | null } | null
  >(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const positionMenu = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const MENU_MAX = 256; // keep in sync with the list's max height
    const GAP = 4;
    const spaceBelow = window.innerHeight - r.bottom;
    // Flip above the field when there isn't room below and there's more room
    // above — common when a tech taps a picker low on a tablet screen.
    const openUp = spaceBelow < MENU_MAX && r.top > spaceBelow;
    setMenuPos({
      left: r.left,
      width: r.width,
      top: openUp ? null : r.bottom + GAP,
      bottom: openUp ? window.innerHeight - r.top + GAP : null,
    });
  }, []);

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label ?? '',
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!isOpen || !q) return options;
    return options.filter((o) => (o.searchText ?? o.label).toLowerCase().includes(q));
  }, [isOpen, query, options]);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // The list lives in a body-level portal, so it's outside containerRef —
      // check it too, otherwise clicking an option would count as "outside".
      if (containerRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setIsOpen(false);
      setActiveIndex(-1);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  // Keep the portaled dropdown aligned to the input while open, including when
  // an ancestor (the inspection form) scrolls.
  useEffect(() => {
    if (!isOpen) return;
    positionMenu();
    const reflow = () => positionMenu();
    window.addEventListener('scroll', reflow, true);
    window.addEventListener('resize', reflow);
    return () => {
      window.removeEventListener('scroll', reflow, true);
      window.removeEventListener('resize', reflow);
    };
  }, [isOpen, positionMenu]);

  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const el = listRef.current.children[activeIndex] as HTMLElement | undefined;
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  const open = () => {
    if (disabled) return;
    positionMenu();
    setIsOpen(true);
    setQuery('');
    setActiveIndex(-1);
  };

  const choose = (opt: SearchableSelectOption) => {
    onChange(opt.value);
    setIsOpen(false);
    setQuery('');
    setActiveIndex(-1);
  };

  const clear = () => {
    onChange('');
    setQuery('');
    setActiveIndex(-1);
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      open();
      return;
    }
    if (!isOpen) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((p) => (p < filtered.length - 1 ? p + 1 : p));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((p) => (p > 0 ? p - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && filtered[activeIndex]) choose(filtered[activeIndex]);
        break;
      case 'Escape':
        setIsOpen(false);
        setActiveIndex(-1);
        break;
    }
  };

  const showClear = !!value && !disabled;
  const listboxId = id ? `${id}-listbox` : undefined;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative flex items-center">
        <Search className="absolute left-3 text-theme-secondary pointer-events-none" size={16} />
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-required={required}
          aria-label={ariaLabel}
          autoComplete="off"
          disabled={disabled}
          value={isOpen ? query : selectedLabel}
          placeholder={placeholder ?? t('actions.searchPlaceholder')}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(-1);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={open}
          onKeyDown={onKeyDown}
          className={`${className ?? DEFAULT_INPUT_CLASS} !pl-9 ${showClear ? '!pr-9' : ''}`}
        />
        {showClear && (
          <button
            type="button"
            onClick={clear}
            aria-label={t('actions.close')}
            className="absolute right-3 p-0.5 rounded-full text-theme-secondary hover:text-theme-primary hover:bg-subtle"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {isOpen && menuPos && createPortal(
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          style={{
            position: 'fixed',
            left: menuPos.left,
            width: menuPos.width,
            maxHeight: 256,
            zIndex: 9999,
            ...(menuPos.top != null ? { top: menuPos.top } : {}),
            ...(menuPos.bottom != null ? { bottom: menuPos.bottom } : {}),
          }}
          className="overflow-auto rounded-lg border border-line bg-surface shadow-lg"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-theme-secondary text-center">
              {t('noResults')}
              {noResultsHint ? (
                <span className="mt-1 block text-xs text-theme-secondary">{noResultsHint}</span>
              ) : null}
            </li>
          ) : (
            filtered.map((opt, i) => (
              <li
                key={opt.value}
                role="option"
                aria-selected={opt.value === value}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(opt)}
                onMouseEnter={() => setActiveIndex(i)}
                className={`px-3 py-2 text-sm cursor-pointer ${
                  i === activeIndex ? 'bg-subtle' : ''
                } ${opt.value === value ? 'font-medium text-blue-500' : 'text-theme-primary'} hover:bg-subtle`}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>,
        document.body,
      )}
    </div>
  );
}
